import React from 'react';
import { Wand2, Loader2, ChevronRight, Check } from 'lucide-react';
import type { Genre, StyleImage, Creation } from '../../types/create';

interface StyleSelectProps {
  isBrowsingStyles: boolean;
  selectedStyle: StyleImage | null;
  selectedCreation: Creation | undefined;
  isHistoryMode: boolean;
  loadingStyles: boolean;
  isTransforming: boolean;
  genres: Genre[];
  selectedGenre: Genre | null;
  onBrowseStyles: () => void;
  onTransform: () => void;
  onSelectGenre: (genre: Genre) => void;
  onSelectStyle: (style: StyleImage) => void;
}

export const StyleSelect: React.FC<StyleSelectProps> = ({
  isBrowsingStyles,
  selectedStyle,
  selectedCreation,
  isHistoryMode,
  loadingStyles,
  isTransforming,
  genres,
  selectedGenre,
  onBrowseStyles,
  onTransform,
  onSelectGenre,
  onSelectStyle
}) => {
  const showInitialState = !isBrowsingStyles && !selectedStyle && !selectedCreation?.styleImageUrl;

  if (showInitialState) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-12 text-center h-full">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
          <Wand2 className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-xl font-bold mb-2">Select Style</h3>
        <p className="text-gray-500 mb-6">Choose from our curated collection of artistic styles.</p>
        <button 
          onClick={onBrowseStyles}
          disabled={isHistoryMode}
          className={`bg-black text-white px-6 md:px-8 py-3 md:py-4 rounded-lg hover:bg-gray-800 transition-colors min-h-[48px] touch-manipulation ${isHistoryMode ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Browse Styles
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold">
          {isHistoryMode ? 'Selected Style' : 'Choose a Style'}
        </h3>
        {(selectedStyle || selectedCreation?.styleImageUrl) && (
          <button 
            onClick={onTransform}
            disabled={isTransforming || isHistoryMode}
            className={`bg-black text-white px-4 md:px-6 py-2 md:py-3 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 min-h-[44px] touch-manipulation ${
              (isTransforming || isHistoryMode) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isTransforming ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Transforming...
              </>
            ) : (
              <>
                Transform
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>

      {isHistoryMode && selectedCreation?.styleImageUrl ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="relative aspect-square max-w-sm w-full rounded-lg overflow-hidden shadow-md">
            <img 
              src={selectedCreation.styleImageUrl} 
              alt="Selected Style"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      ) : loadingStyles ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          Loading styles...
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Genres Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-4 mb-2 scrollbar-hide">
            {genres.map(genre => (
              <button
                key={genre.name}
                onClick={() => onSelectGenre(genre)}
                className={`px-4 py-2.5 rounded-full whitespace-nowrap text-sm font-medium transition-colors min-h-[44px] touch-manipulation ${
                  selectedGenre?.name === genre.name
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {genre.name}
              </button>
            ))}
          </div>

          {/* Styles Grid */}
          <div className="flex-1 overflow-y-auto min-h-[300px]">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {selectedGenre?.styles.map((style) => (
                <div 
                  key={style.name}
                  onClick={() => onSelectStyle(style)}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer group ${
                    selectedStyle?.name === style.name 
                      ? 'ring-4 ring-black ring-offset-2' 
                      : 'hover:opacity-90'
                  }`}
                >
                  <img 
                    src={style.url} 
                    alt={style.name}
                    className="w-full h-full object-cover"
                  />
                  {selectedStyle?.name === style.name && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="bg-white rounded-full p-1">
                        <Check className="w-4 h-4 text-black" />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{style.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

